import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Icon Button API",
  "title": "Icon Button"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Alert = makeShortcode("Alert");
const IconButtonProps = makeShortcode("IconButtonProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="icon_button" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">Icon buttons have an icon included in them.</LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`If children are included with these buttons (usually text), then they render in a style similar to the non-icon buttons.
If no children are included, then they render in the icon-only style.`}</p>
      <Alert mdxType="Alert">
  For icon only buttons you <strong>must</strong> include an{' '}
  <inlineCode className="inverse">aria-label</inlineCode> property that
  accurately describes the function of the button, so that screen-reader users
  will know the button's purpose.
      </Alert>
      <Alert mdxType="Alert">
  For icon buttons with text, an{' '}
  <inlineCode className="inverse">aria-label</inlineCode> is not needed, as the
  function of the button should be described in the visible button text.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  IconButton,
  ButtonVariant,
  ButtonColor,
  ButtonGroup,
} from 'react-magma-dom';
import {
  PriorityHighIcon,
  CheckIcon,
  NotificationsIcon,
  SettingsIcon,
  AddIcon,
} from 'react-magma-icons';

export function Example() {
  return (
    <>
      <ButtonGroup>
        <IconButton aria-label="Notifications" icon={<NotificationsIcon />} />
        <IconButton
          aria-label="Notifications"
          variant={ButtonVariant.solid}
          icon={<NotificationsIcon />}
        />
        <IconButton
          aria-label="Notifications"
          variant={ButtonVariant.link}
          icon={<NotificationsIcon />}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Settings"
          color={ButtonColor.secondary}
          icon={<SettingsIcon />}
        />
        <IconButton
          aria-label="Settings"
          color={ButtonColor.secondary}
          variant={ButtonVariant.solid}
          icon={<SettingsIcon />}
        />
        <IconButton
          aria-label="Settings"
          color={ButtonColor.secondary}
          variant={ButtonVariant.link}
          icon={<SettingsIcon />}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Correct"
          icon={<CheckIcon />}
          color={ButtonColor.subtle}
        />
        <IconButton
          aria-label="Correct"
          color={ButtonColor.subtle}
          variant={ButtonVariant.solid}
          icon={<CheckIcon />}
        />
        <IconButton
          aria-label="Correct"
          color={ButtonColor.subtle}
          variant={ButtonVariant.link}
          icon={<CheckIcon />}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Error"
          color={ButtonColor.danger}
          icon={<PriorityHighIcon />}
        />
        <IconButton
          aria-label="Error"
          color={ButtonColor.danger}
          variant={ButtonVariant.solid}
          icon={<PriorityHighIcon />}
        />
        <IconButton
          aria-label="Error"
          color={ButtonColor.danger}
          variant={ButtonVariant.link}
          icon={<PriorityHighIcon />}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Add"
          color={ButtonColor.success}
          icon={<AddIcon />}
        />
        <IconButton
          aria-label="Add"
          color={ButtonColor.success}
          variant={ButtonVariant.solid}
          icon={<AddIcon />}
        />
        <IconButton
          aria-label="Add"
          color={ButtonColor.success}
          variant={ButtonVariant.link}
          icon={<AddIcon />}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Notifications"
          disabled
          icon={<NotificationsIcon />}
        />
        <IconButton icon={<NotificationsIcon />} aria-label="Notifications">
          Notifications
        </IconButton>
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Settings"
          icon={<SettingsIcon />}
          color={ButtonColor.secondary}
          variant={ButtonVariant.solid}
        >
          Settings
        </IconButton>
        <IconButton
          icon={<CheckIcon />}
          variant={ButtonVariant.link}
          aria-label="Check"
        >
          Submit
        </IconButton>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Assign Icon to Variable`}</h2>
      <p>{`If you find JSX being passed in as a prop directly ugly, you can assign an icon to a variable and pass that in as the prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { IconButton } from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  const icon = <NotificationsIcon />;

  return <IconButton aria-label="Notifications" icon={icon} />;
}
`}</code></pre>
      <h2>{`Custom Size`}</h2>
      <p>{`Icon buttons have default sizes for small, medium and large icon buttons respectively. However, if a pixel size is specified for an icon itself, that size will take precedence.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { IconButton } from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  const icon = <NotificationsIcon size={10} />;

  return (
    <IconButton aria-label="Notifications" icon={icon}>
      With Custom Icon Size
    </IconButton>
  );
}
`}</code></pre>
      <h2>{`Icon Position`}</h2>
      <p>{`Use the optional prop `}<inlineCode parentName="p">{`iconPosition`}</inlineCode>{` to position the icon of the button on either side of the text.
The `}<inlineCode parentName="p">{`iconPosition`}</inlineCode>{` property takes the values `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonColor,
  ButtonIconPosition,
  IconButton,
  ButtonGroup,
} from 'react-magma-dom';
import { NotificationsIcon, InfoIcon } from 'react-magma-icons';

export function Example() {
  return (
    <ButtonGroup>
      <IconButton icon={<NotificationsIcon />} aria-label="Notifications">
        Icon Left
      </IconButton>
      <IconButton
        color={ButtonColor.secondary}
        icon={<InfoIcon />}
        iconPosition={ButtonIconPosition.right}
        aria-label="Information"
      >
        Icon Right
      </IconButton>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Sizes`}</h2>
      <p>{`Sizes for `}<inlineCode parentName="p">{`IconButtons`}</inlineCode>{` include `}<inlineCode parentName="p">{`small`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium`}</inlineCode>{`, and `}<inlineCode parentName="p">{`large`}</inlineCode>{`, with `}<inlineCode parentName="p">{`medium`}</inlineCode>{` being the default value.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonIconPosition,
  ButtonSize,
  IconButton,
  ButtonGroup,
} from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  return (
    <>
      <ButtonGroup>
        <IconButton
          icon={<NotificationsIcon />}
          size={ButtonSize.small}
          aria-label="Notifications"
        />
        <IconButton icon={<NotificationsIcon />} aria-label="Notifications" />
        <IconButton
          icon={<NotificationsIcon />}
          aria-label="Notifications"
          size={ButtonSize.large}
        />
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <IconButton
          aria-label="Notifications"
          icon={<NotificationsIcon />}
          size={ButtonSize.small}
          iconPosition={ButtonIconPosition.left}
        >
          Small Notifications
        </IconButton>
        <IconButton
          aria-label="Notifications"
          icon={<NotificationsIcon />}
          iconPosition={ButtonIconPosition.left}
        >
          Medium Notifications
        </IconButton>
        <IconButton
          aria-label="Notifications"
          icon={<NotificationsIcon />}
          size={ButtonSize.large}
          iconPosition={ButtonIconPosition.left}
        >
          Large Notifications
        </IconButton>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Shape`}</h2>
      <p>{`Icon only buttons have `}<inlineCode parentName="p">{`shape = round`}</inlineCode>{`, by default.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { ButtonShape, IconButton, ButtonGroup } from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  return (
    <ButtonGroup>
      <IconButton icon={<NotificationsIcon />} aria-label="Notifications" />
      <IconButton
        icon={<NotificationsIcon />}
        aria-label="Notifications"
        shape={ButtonShape.fill}
      />
      <IconButton
        icon={<NotificationsIcon />}
        aria-label="Notifications"
        shape={ButtonShape.rightCap}
      />
      <IconButton
        icon={<NotificationsIcon />}
        aria-label="Notifications"
        shape={ButtonShape.leftCap}
      />
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Full-Width Buttons`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isFullWidth`}</inlineCode>{` property will set the button to display full-width. This property does not have any effect on icon-only buttons.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { ButtonIconPosition, IconButton } from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  return (
    <>
      <IconButton
        icon={<NotificationsIcon />}
        isFullWidth
        aria-label="Notifications"
      >
        Full-Width Notifications Btn
      </IconButton>
      <br />
      <IconButton
        icon={<NotificationsIcon />}
        isFullWidth
        iconPosition={ButtonIconPosition.right}
        aria-label="Notifications"
      >
        Full-Width Notifications Btn
      </IconButton>
      <br />
      <IconButton
        icon={<NotificationsIcon />}
        aria-label="Notifications"
        isFullWidth
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  IconButton,
  ButtonVariant,
  ButtonGroup,
  Card,
  CardBody,
} from 'react-magma-dom';
import { PriorityHighIcon, NotificationsIcon } from 'react-magma-icons';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <ButtonGroup>
          <IconButton
            aria-label="Alert"
            variant={ButtonVariant.solid}
            icon={<PriorityHighIcon />}
            isInverse
          />
          <IconButton
            aria-label="Notifications"
            variant={ButtonVariant.solid}
            icon={<NotificationsIcon />}
            isInverse
          />
        </ButtonGroup>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Event Handling`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` event will fire when the button is clicked, or when the enter or spacebar key is pressed.
Events do not fire on disabled buttons.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { IconButton, ButtonGroup } from 'react-magma-dom';
import { NotificationsIcon } from 'react-magma-icons';

export function Example() {
  const [iconButtonCounter, setIconButtonCounter] = React.useState(0);
  const [textIconButtonCounter, setTextIconButtonCounter] = React.useState(0);

  return (
    <>
      <p>
        <strong>Icon Counter: </strong> {iconButtonCounter} |{' '}
        <strong>Text Icon Counter: </strong> {textIconButtonCounter}
      </p>
      <ButtonGroup>
        <IconButton
          icon={<NotificationsIcon />}
          aria-label="Notifications"
          onClick={() => {
            setIconButtonCounter(count => count + 1);
          }}
        >
          Click me
        </IconButton>
        <IconButton
          icon={<NotificationsIcon />}
          aria-label="Notifications"
          onClick={() => {
            setTextIconButtonCounter(count => count + 1);
          }}
        >
          Click me
        </IconButton>
        <IconButton
          disabled
          icon={<NotificationsIcon />}
          aria-label="Notifications"
          onClick={() => {
            setIconButtonCounter(count => count + 1);
          }}
        >
          Click me if you can
        </IconButton>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Testing`}</h2>
      <p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the button element for easier querying in tests.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<IconButton testId="test-id" aria-label="Test">
  <svg
    aria-hidden="true"
    class="icon"
    height="18"
    width="18"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 21.17 23.56"
  >
    <path
      d="M21.9,10.31c0-4.8-5.34-6.49-5.34-6.49V2.22h-2v1.6S9.22,5.51,9.22,10.31,7,20.69,5,20.69v1.9h7.38a3.2,3.2,0,0,0,6.39,0h7.39v-1.9c-2,0-4.24-5.59-4.24-10.38Z"
      transform="translate(-4.98 -2.22)"
    ></path>
  </svg>
</IconButton>
`}</code></pre>
      <h2>{`Icon Button Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`button`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button"
        }}>{`standard button attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`button`}</inlineCode>{` element that gets rendered.`}</p>
      <IconButtonProps mdxType="IconButtonProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      